"use client";

import { useState } from "react";
import {
  CopyButton,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  checklistTsv,
  ItemRow,
} from "@/components/tools/research-discovery/prisma-row";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ChecklistKind,
  type Entries,
  entryFor,
  entryKey,
  filledCount,
  type ItemEntry,
  itemsFor,
} from "@/lib/tool-kit/research-discovery/prisma-items";

/** English strings. A translated content file overrides these through `ui`. */
export const PRISMA_CHECKLIST_STRINGS = {
  worksheetTitle: "PRISMA 2020 Checklist",
  abstractTitle: "PRISMA 2020 for Abstracts Checklist",
  reviewHeading: "Your review",
  titleLabel: "Title of the review",
  titlePlaceholder:
    "e.g. Mindfulness for exam anxiety in students: a systematic review",
  authorsLabel: "Completed by",
  authorsPlaceholder: "Name or initials",
  dateLabel: "Date",
  kindLabel: "Checklist",
  kinds: {
    full: "Full checklist (27 items)",
    abstract: "Abstract checklist (12 items)",
  },
  columns: {
    section: "Section and topic",
    item: "Item #",
    text: "Checklist item",
    location: "Location where item is reported",
    status: "Status",
  },
  locationLabel: "Location for item {item}",
  locationPlaceholder: "Page or section",
  statusLabel: "Status of item {item}",
  statusNone: "–",
  statuses: {
    reported: "Reported",
    partly: "Partly reported",
    na: "Not applicable",
  },
  sections: {
    title: "Title",
    abstract: "Abstract",
    introduction: "Introduction",
    methods: "Methods",
    results: "Results",
    discussion: "Discussion",
    other: "Other information",
    background: "Background",
  },
  progressHeading: "Progress",
  statFilled: "Items filled in",
  statTotal: "Items in this checklist",
  progressNote:
    "An item counts as filled in when it has a location or a status. A review is not finished when the checklist is full: the page or section you cite should contain the reporting the item asks for.",
  print: "Print checklist",
  copyTable: "Copy as table",
  copied: "Copied!",
  reset: "Clear this checklist",
  attribution:
    "The checklist items are from Page MJ, McKenzie JE, Bossuyt PM, Boutron I, Hoffmann TC, Mulrow CD, et al. The PRISMA 2020 statement: an updated guideline for reporting systematic reviews. BMJ 2021;372:n71. doi:10.1136/bmj.n71. Licensed under CC BY 4.0 (creativecommons.org/licenses/by/4.0/); the location and status columns are additions. See prisma-statement.org.",
  copyMeta: "{review} — {authors}, {date}",
  items: {
    "1": "Identify the report as a systematic review.",
    "2": "See the PRISMA 2020 for Abstracts checklist (table 2).",
    "3": "Describe the rationale for the review in the context of existing knowledge.",
    "4": "Provide an explicit statement of the objective(s) or question(s) the review addresses.",
    "5": "Specify the inclusion and exclusion criteria for the review and how studies were grouped for the syntheses.",
    "6": "Specify all databases, registers, websites, organisations, reference lists and other sources searched or consulted to identify studies. Specify the date when each source was last searched or consulted.",
    "7": "Present the full search strategies for all databases, registers and websites, including any filters and limits used.",
    "8": "Specify the methods used to decide whether a study met the inclusion criteria of the review, including how many reviewers screened each record and each report retrieved, whether they worked independently, and if applicable, details of automation tools used in the process.",
    "9": "Specify the methods used to collect data from reports, including how many reviewers collected data from each report, whether they worked independently, any processes for obtaining or confirming data from study investigators, and if applicable, details of automation tools used in the process.",
    "10a":
      "List and define all outcomes for which data were sought. Specify whether all results that were compatible with each outcome domain in each study were sought (e.g. for all measures, time points, analyses), and if not, the methods used to decide which results to collect.",
    "10b":
      "List and define all other variables for which data were sought (e.g. participant and intervention characteristics, funding sources). Describe any assumptions made about any missing or unclear information.",
    "11": "Specify the methods used to assess risk of bias in the included studies, including details of the tool(s) used, how many reviewers assessed each study and whether they worked independently, and if applicable, details of automation tools used in the process.",
    "12": "Specify for each outcome the effect measure(s) (e.g. risk ratio, mean difference) used in the synthesis or presentation of results.",
    "13a":
      "Describe the processes used to decide which studies were eligible for each synthesis (e.g. tabulating the study intervention characteristics and comparing against the planned groups for each synthesis (item #5)).",
    "13b":
      "Describe any methods required to prepare the data for presentation or synthesis, such as handling of missing summary statistics, or data conversions.",
    "13c":
      "Describe any methods used to tabulate or visually display results of individual studies and syntheses.",
    "13d":
      "Describe any methods used to synthesise results and provide a rationale for the choice(s). If meta-analysis was performed, describe the model(s), method(s) to identify the presence and extent of statistical heterogeneity, and software package(s) used.",
    "13e":
      "Describe any methods used to explore possible causes of heterogeneity among study results (e.g. subgroup analysis, meta-regression).",
    "13f":
      "Describe any sensitivity analyses conducted to assess robustness of the synthesised results.",
    "14": "Describe any methods used to assess risk of bias due to missing results in a synthesis (arising from reporting biases).",
    "15": "Describe any methods used to assess certainty (or confidence) in the body of evidence for an outcome.",
    "16a":
      "Describe the results of the search and selection process, from the number of records identified in the search to the number of studies included in the review, ideally using a flow diagram (see fig 1).",
    "16b":
      "Cite studies that might appear to meet the inclusion criteria, but which were excluded, and explain why they were excluded.",
    "17": "Cite each included study and present its characteristics.",
    "18": "Present assessments of risk of bias for each included study.",
    "19": "For all outcomes, present, for each study: (a) summary statistics for each group (where appropriate) and (b) an effect estimate and its precision (e.g. confidence/credible interval), ideally using structured tables or plots.",
    "20a":
      "For each synthesis, briefly summarise the characteristics and risk of bias among contributing studies.",
    "20b":
      "Present results of all statistical syntheses conducted. If meta-analysis was done, present for each the summary estimate and its precision (e.g. confidence/credible interval) and measures of statistical heterogeneity. If comparing groups, describe the direction of the effect.",
    "20c":
      "Present results of all investigations of possible causes of heterogeneity among study results.",
    "20d":
      "Present results of all sensitivity analyses conducted to assess the robustness of the synthesised results.",
    "21": "Present assessments of risk of bias due to missing results (arising from reporting biases) for each synthesis assessed.",
    "22": "Present assessments of certainty (or confidence) in the body of evidence for each outcome assessed.",
    "23a":
      "Provide a general interpretation of the results in the context of other evidence.",
    "23b": "Discuss any limitations of the evidence included in the review.",
    "23c": "Discuss any limitations of the review processes used.",
    "23d":
      "Discuss implications of the results for practice, policy, and future research.",
    "24a":
      "Provide registration information for the review, including register name and registration number, or state that the review was not registered.",
    "24b":
      "Indicate where the review protocol can be accessed, or state that a protocol was not prepared.",
    "24c":
      "Describe and explain any amendments to information provided at registration or in the protocol.",
    "25": "Describe sources of financial or non-financial support for the review, and the role of the funders or sponsors in the review.",
    "26": "Declare any competing interests of review authors.",
    "27": "Report which of the following are publicly available and where they can be found: template data collection forms; data extracted from included studies; data used for all analyses; analytic code; any other materials used in the review.",
  },
  abstractItems: {
    "1": "Identify the report as a systematic review.",
    "2": "Provide an explicit statement of the main objective(s) or question(s) the review addresses.",
    "3": "Specify the inclusion and exclusion criteria for the review.",
    "4": "Specify the information sources (e.g. databases, registers) used to identify studies and the date when each was last searched.",
    "5": "Specify the methods used to assess risk of bias in the included studies.",
    "6": "Specify the methods used to present and synthesise results.",
    "7": "Give the total number of included studies and participants and summarise relevant characteristics of studies.",
    "8": "Present results for main outcomes, preferably indicating the number of included studies and participants for each. If meta-analysis was done, report the summary estimate and confidence/credible interval. If comparing groups, indicate the direction of the effect (i.e. which group is favoured).",
    "9": "Provide a brief summary of the limitations of the evidence included in the review (e.g. study risk of bias, inconsistency and imprecision).",
    "10": "Provide a general interpretation of the results and important implications.",
    "11": "Specify the primary source of funding for the review.",
    "12": "Provide the register name and registration number.",
  },
  topics: {
    title: "Title",
    abstract: "Abstract",
    rationale: "Rationale",
    objectives: "Objectives",
    eligibilityCriteria: "Eligibility criteria",
    informationSources: "Information sources",
    searchStrategy: "Search strategy",
    selectionProcess: "Selection process",
    dataCollectionProcess: "Data collection process",
    dataItems: "Data items",
    studyRiskOfBiasAssessment: "Study risk of bias assessment",
    effectMeasures: "Effect measures",
    synthesisMethods: "Synthesis methods",
    reportingBiasAssessment: "Reporting bias assessment",
    certaintyAssessment: "Certainty assessment",
    studySelection: "Study selection",
    studyCharacteristics: "Study characteristics",
    riskOfBiasInStudies: "Risk of bias in studies",
    resultsOfIndividualStudies: "Results of individual studies",
    resultsOfSyntheses: "Results of syntheses",
    reportingBiases: "Reporting biases",
    certaintyOfEvidence: "Certainty of evidence",
    discussion: "Discussion",
    registrationAndProtocol: "Registration and protocol",
    support: "Support",
    competingInterests: "Competing interests",
    availabilityOfDataCodeAndOtherMaterials:
      "Availability of data, code, and other materials",
    riskOfBias: "Risk of bias",
    synthesisOfResults: "Synthesis of results",
    includedStudies: "Included studies",
    limitationsOfEvidence: "Limitations of evidence",
    interpretation: "Interpretation",
    funding: "Funding",
    registration: "Registration",
  },
} as const;

export type PrismaChecklistStrings = typeof PRISMA_CHECKLIST_STRINGS;

export default function PrismaChecklistWidget() {
  const strings = useToolUi(PRISMA_CHECKLIST_STRINGS);
  const fmt = useToolNumber();
  const [kind, setKind] = useState<ChecklistKind>("full");
  const [entries, setEntries] = useState<Entries>({});
  const [review, setReview] = useState({ title: "", authors: "", date: "" });
  const items = itemsFor(kind);
  const filled = filledCount(entries, kind);
  const title =
    kind === "full" ? strings.worksheetTitle : strings.abstractTitle;

  function update(id: string, next: ItemEntry) {
    setEntries((current) => ({ ...current, [entryKey(kind, id)]: next }));
  }

  function clear() {
    setEntries((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([key]) => !key.startsWith(`${kind}:`))
      )
    );
  }

  const copy = [
    title,
    t(strings.copyMeta, {
      review: review.title,
      authors: review.authors,
      date: review.date,
    }),
    "",
    checklistTsv(strings, kind, entries),
  ].join("\n");

  return (
    <div className="space-y-6">
      <h2 className="hidden font-semibold text-2xl text-foreground print:block">
        {title}
      </h2>
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.kindLabel}
          name="pck-kind"
          onChange={setKind}
          options={[
            { value: "full", label: strings.kinds.full },
            { value: "abstract", label: strings.kinds.abstract },
          ]}
          value={kind}
        />
      </div>
      <section className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="pck-title"
          label={strings.titleLabel}
          onChange={(value) => setReview((c) => ({ ...c, title: value }))}
          placeholder={strings.titlePlaceholder}
          value={review.title}
        />
        <TextField
          id="pck-authors"
          label={strings.authorsLabel}
          onChange={(value) => setReview((c) => ({ ...c, authors: value }))}
          placeholder={strings.authorsPlaceholder}
          value={review.authors}
        />
        <TextField
          id="pck-date"
          label={strings.dateLabel}
          onChange={(value) => setReview((c) => ({ ...c, date: value }))}
          type="date"
          value={review.date}
        />
      </section>
      <div className="overflow-x-auto rounded-lg border border-border bg-card print:overflow-visible print:border-none">
        <table className={TABLE}>
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.columns.section}
              </th>
              <th className={TH} scope="col">
                {strings.columns.item}
              </th>
              <th className={TH} scope="col">
                {strings.columns.text}
              </th>
              <th className={TH} scope="col">
                {strings.columns.location}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <ItemRow
                entry={entryFor(entries, kind, item.id)}
                index={index}
                key={item.id}
                kind={kind}
                onChange={(next) => update(item.id, next)}
                strings={strings}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className={HINT}>{strings.attribution}</p>
      <section aria-live="polite" className="space-y-4 print:hidden">
        <h2 className={WIDGET_H2}>{strings.progressHeading}</h2>
        <StatGrid>
          <StatCard emphasis label={strings.statFilled} value={fmt(filled)} />
          <StatCard label={strings.statTotal} value={fmt(items.length)} />
        </StatGrid>
        <p className={HINT}>{strings.progressNote}</p>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyTable}
            text={copy}
          />
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.reset}
          </button>
        </div>
      </section>
    </div>
  );
}
